import { GUEST_JOURNEY_STAGE_SLIDER_MAX } from 'api/reports-service/guest-journey-settings.utils';
import * as yup from 'yup';

/** Max characters for a configured stage name. */
export const STAGE_NAME_MAX_LENGTH = 40;

const stageNameSchema = () =>
  yup.string().trim().required().max(STAGE_NAME_MAX_LENGTH);

/**
 * Spend boundaries are dollar amounts, so cents are allowed — but no finer than
 * two decimal places. Guarded numerically (not via `.integer()`) to avoid
 * float false-negatives, e.g. `19.99 * 100 === 1999.0000000000002`.
 */
const isCurrencyAmount = (value?: number) =>
  value == null || Math.abs(Math.round(value * 100) - value * 100) < 1e-9;

/**
 * Validates the full `GuestJourneySettings` payload before it is saved to the
 * BE. Names must be present and reasonably short; the four boundaries sit inside
 * the slider range, with the lower boundary strictly below the upper on each
 * axis (visits floor at 1, spend at 0; both cap at the slider max). Visit
 * boundaries are whole numbers; spend boundaries are dollar amounts allowing up
 * to two decimal places. Standard rule messages come from the global
 * `yup.setLocale`.
 */
export const guestJourneySettingsSchema = yup.object({
  visitTier1Label: stageNameSchema(),
  visitTier2Label: stageNameSchema(),
  visitTier3Label: stageNameSchema(),
  spendTier1Label: stageNameSchema(),
  spendTier2Label: stageNameSchema(),
  spendTier3Label: stageNameSchema(),
  visitTier1Threshold: yup
    .number()
    .integer()
    .required()
    .min(1)
    .max(GUEST_JOURNEY_STAGE_SLIDER_MAX)
    .lessThan(yup.ref('visitTier2Threshold')),
  visitTier2Threshold: yup
    .number()
    .integer()
    .required()
    .max(GUEST_JOURNEY_STAGE_SLIDER_MAX)
    .moreThan(yup.ref('visitTier1Threshold')),
  spendTier1Threshold: yup
    .number()
    .required()
    .min(0)
    .max(GUEST_JOURNEY_STAGE_SLIDER_MAX)
    .lessThan(yup.ref('spendTier2Threshold'))
    .test(
      'currencyPrecision',
      'Enter an amount with up to two decimal places',
      isCurrencyAmount,
    ),
  spendTier2Threshold: yup
    .number()
    .required()
    .max(GUEST_JOURNEY_STAGE_SLIDER_MAX)
    .moreThan(yup.ref('spendTier1Threshold'))
    .test(
      'currencyPrecision',
      'Enter an amount with up to two decimal places',
      isCurrencyAmount,
    ),
});
